import { useState, useEffect } from 'react';
import {
  DownloadIcon,
  CircleNotchIcon,
  WarningCircleIcon,
  ArrowsOutIcon,
  XIcon,
  ArrowsOutLineHorizontalIcon,
  ArrowsInLineHorizontalIcon,
} from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { VirtualizedTable } from './VirtualizedTable';
import { useXlsxParser } from './use-xlsx-parser';
import type { XlsxViewerProps } from './types';

export function XlsxViewer({ url, fileName, height = 450 }: XlsxViewerProps) {
  const { sheets, isLoading, error } = useXlsxParser(url);
  const [activeSheet, setActiveSheet] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === 'undefined' ? 800 : window.innerHeight,
  );

  useEffect(() => {
    if (!isFullscreen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setIsFullscreen(false);
      }
    }

    function handleResize() {
      setViewportHeight(window.innerHeight);
    }

    handleResize();
    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isFullscreen]);

  const hasMultipleSheets = sheets.length > 1;
  const defaultSheetName = sheets[0]?.name ?? '';
  const activeSheetName = sheets.some((sheet) => sheet.name === activeSheet)
    ? activeSheet
    : defaultSheetName;
  const currentSheet = sheets.find((sheet) => sheet.name === activeSheetName);
  const headerHeight = 44;
  const tabsBarHeight = hasMultipleSheets ? 40 : 0;
  const tableHeight =
    typeof height === 'number' ? height - headerHeight - tabsBarHeight : 406;

  const fullscreenHeaderHeight = 56;
  const fullscreenTabsHeight = hasMultipleSheets ? 40 : 0;
  const fullscreenTableHeight = Math.max(
    200,
    Math.floor(viewportHeight - fullscreenHeaderHeight - fullscreenTabsHeight),
  );

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center border rounded-md bg-muted/10"
        style={{ height }}
      >
        <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 border rounded-md bg-muted/10"
        style={{ height }}
      >
        <div className="flex items-center gap-2 text-destructive">
          <WarningCircleIcon className="h-5 w-5" />
          <span className="text-sm">{error}</span>
        </div>
        <Button variant="outline" size="sm" asChild>
          <a href={url} download={fileName}>
            <DownloadIcon className="h-4 w-4 mr-1.5" />
            Download File
          </a>
        </Button>
      </div>
    );
  }

  if (sheets.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 border rounded-md bg-muted/10"
        style={{ height }}
      >
        <span className="text-sm text-muted-foreground">
          The Excel file appears to be empty
        </span>
        <Button variant="outline" size="sm" asChild>
          <a href={url} download={fileName}>
            <DownloadIcon className="h-4 w-4 mr-1.5" />
            Download File
          </a>
        </Button>
      </div>
    );
  }

  const singleSheetLabel = (
    <div className="flex items-center h-10 px-3 gap-2">
      <span className="text-sm font-medium">{sheets[0].name}</span>
      <span className="text-xs text-muted-foreground">
        ({sheets[0].rows.length} {sheets[0].rows.length === 1 ? 'row' : 'rows'})
      </span>
    </div>
  );

  const activeSheetSummary = (
    <div className="flex items-center h-10 px-3 gap-2">
      <span className="text-sm font-medium">{activeSheetName}</span>
      <span className="text-xs text-muted-foreground">
        ({currentSheet?.rows.length ?? 0}{' '}
        {(currentSheet?.rows.length ?? 0) === 1 ? 'row' : 'rows'})
      </span>
    </div>
  );

  const sheetTabs = (
    <div className="flex items-end h-10 gap-1 px-2">
      {sheets.map((sheet) => (
        <Tooltip key={sheet.name}>
          <TooltipTrigger asChild>
            <button
              onClick={() => setActiveSheet(sheet.name)}
              className={cn(
                'h-8 px-3 text-sm rounded-t-md border border-b-0 transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                activeSheetName === sheet.name
                  ? 'bg-background text-foreground border-border font-semibold'
                  : 'bg-muted/70 text-foreground/70 border-border/60 hover:bg-muted/80 hover:text-foreground font-medium',
              )}
            >
              {sheet.name}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {sheet.rows.length} {sheet.rows.length === 1 ? 'row' : 'rows'}
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  );

  const fullscreenOverlay = isFullscreen && (
    <div
      className="fixed inset-0 bg-background flex flex-col"
      style={{ zIndex: 2000 }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
        <h2 className="text-base font-medium">{fileName}</h2>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? (
                  <ArrowsInLineHorizontalIcon className="h-4 w-4" />
                ) : (
                  <ArrowsOutLineHorizontalIcon className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isExpanded ? 'Fit columns' : 'Expand columns to content'}
            </TooltipContent>
          </Tooltip>
          <Button variant="outline" size="sm" className="h-8" asChild>
            <a href={url} download={fileName}>
              <DownloadIcon className="h-4 w-4 mr-1.5" />
              Download
            </a>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => setIsFullscreen(false)}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        {currentSheet && (
          <VirtualizedTable
            sheet={currentSheet}
            height={fullscreenTableHeight}
            expanded={isExpanded}
          />
        )}
      </div>
      {hasMultipleSheets && (
        <div className="border-t bg-muted/50">{sheetTabs}</div>
      )}
    </div>
  );

  const actionButtons = (
    <div className="flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ArrowsInLineHorizontalIcon className="h-4 w-4" />
            ) : (
              <ArrowsOutLineHorizontalIcon className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {isExpanded ? 'Fit columns' : 'Expand columns to content'}
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => setIsFullscreen(true)}
          >
            <ArrowsOutIcon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Fullscreen</TooltipContent>
      </Tooltip>
      <Button variant="ghost" size="sm" className="h-8 px-2" asChild>
        <a href={url} download={fileName}>
          <DownloadIcon className="h-4 w-4 mr-1.5" />
          <span className="text-sm">Download</span>
        </a>
      </Button>
    </div>
  );

  return (
    <>
      {fullscreenOverlay}
      <div
        className="flex flex-col border rounded-md overflow-hidden bg-background"
        style={{ height }}
      >
        <div className="flex items-center justify-between border-b bg-muted/30 px-2">
          {hasMultipleSheets ? activeSheetSummary : singleSheetLabel}
          {actionButtons}
        </div>
        <div className="flex-1 overflow-hidden">
          {currentSheet && (
            <VirtualizedTable
              sheet={currentSheet}
              height={tableHeight}
              expanded={isExpanded}
            />
          )}
        </div>
        {hasMultipleSheets && (
          <div className="border-t bg-muted/50">{sheetTabs}</div>
        )}
      </div>
    </>
  );
}
